import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Dialog,
  DialogBackdrop,
  DialogPanel,
} from '@headlessui/react'
import { MagnifyingGlassIcon } from '@heroicons/react/20/solid'
import { useEffect, useMemo, useRef } from 'react'
import { Badge } from './Badge.jsx'

// CommandPalette (Wave 47p) — full visual rewrite. Earlier versions
// relied on Tailwind `mx-auto` to center the panel inside a `fixed
// inset-0` container, which silently failed in this build pipeline
// (the panel rendered flush against the viewport's left edge with no
// padding). The reliable fix is flex-center on the parent + explicit
// max-width on the panel; both are expressed via studio CSS classes
// in studio.css under `.studio-cmdk-*` so the layout doesn't depend
// on whether Tailwind picks up arbitrary-value classes.

function getStatusTone(status) {
  if (status === 'published') return 'success'
  if (status === 'scheduled') return 'planning'
  return 'neutral'
}

const GROUP_ORDER = [
  'Workspace',
  'Resume',
  'Drafts',
  'Queue',
  'Delivered',
  'Research',
  'Reports',
  'Proof',
  'Command',
]

function groupResults(results) {
  const groups = results.reduce((acc, item) => {
    const group = item.category || item.type || 'Command'
    return {
      ...acc,
      [group]: [...(acc[group] || []), item],
    }
  }, {})
  return Object.fromEntries(
    Object.entries(groups).sort(([a], [b]) => {
      const ai = GROUP_ORDER.indexOf(a)
      const bi = GROUP_ORDER.indexOf(b)
      if (ai === -1 && bi === -1) return a.localeCompare(b)
      if (ai === -1) return 1
      if (bi === -1) return -1
      return ai - bi
    })
  )
}

export function CommandPalette({
  open,
  query,
  onQueryChange,
  results,
  onSelect,
  onClose,
}) {
  const previouslyFocused = useRef(null)
  const groups = useMemo(() => groupResults(results), [results])

  useEffect(() => {
    if (open) {
      previouslyFocused.current = document.activeElement
      return
    }
    if (previouslyFocused.current?.focus) {
      previouslyFocused.current.focus()
    }
  }, [open])

  return (
    <Dialog
      open={open}
      onClose={() => onClose(false)}
      className="studio-cmdk-root"
    >
      <DialogBackdrop className="studio-cmdk-backdrop" />
      <div className="studio-cmdk-positioner">
        <DialogPanel className="studio-cmdk-panel">
          <Combobox
            onChange={(item) => {
              if (!item) return
              onSelect(item)
              onClose(false)
            }}
          >
            <div className="studio-cmdk-input-row">
              <MagnifyingGlassIcon
                className="studio-cmdk-search-icon"
                aria-hidden="true"
              />
              <ComboboxInput
                autoFocus
                aria-label="Search workspaces, records, or actions"
                className="studio-cmdk-input"
                placeholder="Jump to a workspace, record, or action"
                value={query}
                onChange={(event) => onQueryChange(event.target.value)}
              />
              <span className="studio-cmdk-esc">Esc</span>
            </div>

            <div className="studio-cmdk-results">
              {results.length === 0 ? (
                <div className="studio-cmdk-empty">
                  <strong>Nothing matches.</strong>
                  <span>
                    Try a capture, proof asset, draft, or infrastructure action.
                  </span>
                </div>
              ) : (
                <ComboboxOptions static>
                  {Object.entries(groups).map(([group, items]) => (
                    <div key={group} className="studio-cmdk-group">
                      <div className="studio-cmdk-group-head">{group}</div>
                      {items.map((item) => (
                        <ComboboxOption
                          key={item.id}
                          value={item}
                          className="studio-cmdk-option"
                        >
                          <div className="studio-cmdk-option-text">
                            <div className="studio-cmdk-option-label">
                              {item.label}
                            </div>
                            {item.meta ? (
                              <div className="studio-cmdk-option-meta">
                                {item.meta}
                              </div>
                            ) : null}
                          </div>
                          {item.postStatus ? (
                            <Badge tone={getStatusTone(item.postStatus)}>
                              {item.postStatus}
                            </Badge>
                          ) : null}
                        </ComboboxOption>
                      ))}
                    </div>
                  ))}
                </ComboboxOptions>
              )}
            </div>
          </Combobox>
        </DialogPanel>
      </div>
    </Dialog>
  )
}
